import { UIIcon } from '../../../config/icons';
import { YPaymentAmount, YPaymentCurrency } from '../../y/amount';
import { $trigger } from '../../y/events';
import { $i18n } from '../../y/i18n';
import { yIsRegExp } from '../../y/object';
import { $el } from '../elements';
import { onKey, UIKey } from '../key';
import { UIView } from '../view';
import { UITextBasedInput, UITextBasedInputOptions, UITextInputValidator } from './input-base';

export interface UITextInputOptions extends UITextBasedInputOptions<string> {
    clear_button?: boolean;
    null_if_empty?: boolean;
    undefined_if_empty?: boolean;
    type?: UITextInputType;
    keep_ws?: boolean;
    validator?: UITextInputValidator;
}

export namespace textInputTypes {
    export const email: UITextInputOptions = {
        autocapitalize: false,
        validator: /^[a-z0-9!#$%&'*+\/=?^_`{|}~.-]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i,
        invalid_message: $i18n.ui.inv_email
    }

    export const password: UITextInputOptions = {
        sensitive: true,
        autocapitalize: false,
    }

    export const new_password: UITextInputOptions = {
        sensitive: true,
        autocapitalize: false,
        validator: (text) => !text || text.length >= 6, // TODO: empty texts shouldn't need to pass the validator
        invalid_message: $i18n.ui.inv_password
    }

    export const digits: UITextInputOptions = {
        fixer: (s) => [s.replace(/\D/g, ''), undefined]
    }

    export const phone: UITextInputOptions = {
        fixer: (s) => [s.replace(/[^0-9,.* +()-]/g, ''), undefined]
    }
}

export type UITextInputType = keyof typeof textInputTypes;

export class UITextInput extends UITextBasedInput<string> {
    private _value: string;
    private _validator?: UITextInputValidator;
    private _keep_ws?: boolean;
    private _null_if_empty?: boolean;
    private _undefined_if_empty?: boolean;

    constructor(options: UITextInputOptions = {}) {
        if(options.type)
            options = Object.assign({}, textInputTypes[options.type], options);

        if(options.clear_button) {
            const button = $el.ICON(UIIcon.XMARK, 'u-clickable');
            options.r_deco = button;
            button.addEventListener('click', () => {
                this.setText('', true);
            });
        }

        super(options);

        this._validator = options.validator;
        this._keep_ws = options.keep_ws;
        this._null_if_empty = options.null_if_empty;
        this._undefined_if_empty = options.undefined_if_empty;
        this._value = '';

        this.doCheck();
    }

    protected onText(text: string, notify: boolean) {
        if(!this._keep_ws)
            text = text.trim();
        this._value = text;
        $trigger(this, 'value', text);
        if(notify)
            $trigger(this, 'input');

        this.fixDesign();
        this.doCheck();
    }

    protected doCheck() {
        const validator = this._validator;
        this.invalid = validator ? !(yIsRegExp(validator) ? (!this._value || validator.test(this._value)) : validator(this._value, this._value)) : false;
        this.setOkay(!this.invalid && !(this.required && !this._value));
    }

    get value() {
        if(this._null_if_empty && !this._value)
            return null as any;
        if(this._undefined_if_empty && !this._value)
            return undefined as any;
        return this._value;
    }

    set value(value: string) {
        this.setText(value || '', false);
    }
}

// interface UIOptionsInputOptions<Key> extends UIBaseInputOptions
// {
//     delay?: number;
//     menu_class?: string;
//     autoresize?: boolean;
//     popup_options?: UIPopupOptions;
//     generator?: UIDropdownViewGenerator<Key>;
// }

// export class UIOptionsInput<Key> extends UIBaseInput<Key|undefined>
// {
//     private _value: Key | undefined;
//     private _text!: string;

//     private _wrapper: UIAnyInputWrapper;
//     private _values: (term:string) => Promise<UIValue<Key>[]>;
//     private _token?: {};
//     private _popup?: UIDropdown;

//     private _delay: number;
//     private _menu_class?: string;
//     private _popup_options?: UIPopupOptions;
//     private _generator?: UIDropdownViewGenerator<any>;

//     on_option?: (v: UIValue<Key>) => void;

//     constructor(values: (term:string) => Promise<UIValue<Key>[]>, options: UIOptionsInputOptions<Key>)
//     {
//         // TODO: add autocapitalize
//         const wrapper = makeInputWrapper(options);
//         const input = wrapper.element;

//         super(options, input);
//         this._wrapper = wrapper;

//         super(options, input);
//         this._values = values;

//         this._delay = (options.delay === undefined) ? 300 : options.delay;
//         this._menu_class = options.menu_class;
//         this._popup_options = options.popup_options;
//         this._generator = options.generator;

//         this.input_elem.addEventListener('input', (e) => this.onInput(e));

//         this.input_elem.addEventListener('blur', (e) => {
//             this.classList.remove('f-focus');
//             $trigger(this, 'blur');
//         });

//         this.input_elem.addEventListener('focus', (e) => {
//             this.classList.add('f-focus');
//             $trigger(this, 'focus');
//         });

//         this.doCheck();
//     }

//     onInput(e: Event)
//     {
//         this.setDirty(true);
//         const wrapper = this._wrapper;
//         const text = wrapper.text;

//         if(this._popup) {
//             this._popup.close();
//             this._popup = undefined;
//         }

//         if(text) {
//             const values = this._values;
//             const token = this._token = {};

//             const func = () => {
//                 if(token !== this._token) return;
//                 values(text).then((options) => {
//                     if(token !== this._token) return;

//                     const popup = new UIDropdown(options, { generator: this._generator });
//                     popup.on_choose = v => {
//                         this.value = v.key;
//                         this.on_option && this.on_option(v);
//                     };
//                     if(this._menu_class)
//                         popup.classList.add(this._menu_class);
//                     this._popup && this._popup.close();
//                     this._popup = popup;
//                     popup.open(this, this._popup_options || { position: UIRelPos.Below|UIRelPos.End, capture_mouse: true, adjust_size: true, match_size: true });
//                 });
//             };

//             if(this._delay)
//                 setTimeout(func, this._delay);
//             else
//                 func();
//         }

//         this._doSetText(text);
//     }

//     private _doSetText(text: string) 
//     {
//         this._text = text;
//         this._value = undefined;
//         this.fixDesign();
//         this.doCheck();

//         $trigger(this, 'value', this._value);
//     }

//     protected doCheck()
//     {
//         this.invalid = false;
//         this.setOkay(!this.invalid && !(this.required && !this._value));
//     }

//     get value(): Key | undefined
//     {
//         return this._value;
//     }

//     set value(value: Key | undefined)
//     {
//         this._value = value;
//         this._text = value ? value +'' : '';
//         this._wrapper.text = this._text;
//         this.fixDesign();
//         this.doCheck();
//     }

//     get text(): string
//     {
//         return this._text;
//     }

//     set text(text: string)
//     {
//         this._text = text;
//         this._wrapper.text = text;
//         this.fixDesign();
//     }

//     get is_empty()
//     {
//         return !this._text || !this._text.length;
//     }

//     selectAll()
//     {
//         this._wrapper.select();
//     }
// }

interface UINumberInputOptions extends UITextBasedInputOptions<number> {
    min?: number;
    max?: number;
    decimal?: boolean;
    buttons?: boolean;
    scale?: number;
}

export class UINumberInput extends UITextBasedInput<number | undefined> {
    private _value?: number;
    private _min: number;
    private _max: number;
    private _decimal?: boolean;
    private _scale: number;

    constructor(options: UINumberInputOptions = {}) {
        let min: number;
        let max: number;
        let regexp: RegExp;

        if(options.decimal || (options.scale && options.scale > 1)) {
            min = options.min == undefined ? -Number.MAX_VALUE : options.min;
            max = options.max == undefined ? Number.MAX_VALUE : options.max;
            regexp = /[^-0-9.]/g;
        } else {
            min = options.min || 0;
            max = options.max == undefined ? Number.MAX_VALUE : options.max;
            regexp = min < 0 ? /[^-0-9]/g : /\D/g;
        }

        options.fixer = (text, pos) => {
            const fixed = text.replace(regexp, '');
            if(text != fixed)
                return [fixed, pos > 0 ? pos - 1 : pos];
            return;
        }

        super(options);

        this._scale = options.scale || 1;
        this._decimal = options.decimal;
        this._min = min;
        this._max = max;

        if(options.buttons) {
            const up_button = $el.ICON(UIIcon.CARET_UP, 'r-deco-up');
            const down_button = $el.ICON(UIIcon.CARET_DOWN, 'r-deco-down');

            this.ui_add([up_button, down_button]);

            up_button.addEventListener('click', () => { this.increment() });
            down_button.addEventListener('click', () => { this.decrement() });
        }

        onKey(this, e => {
            if(e.key === UIKey.Down) {
                this.decrement();
                return true;
            } else if(e.key === UIKey.Up) {
                this.increment();
                return true;
            }
            return;
        });

        this.doCheck();
    }

    decrement() {
        const current = this.value || 0;
        if(current > this._min) {
            this.setDirty(true);
            this.value = current - this._scale;
            $trigger(this, 'value', this.value);
            $trigger(this, 'input');
        }
    }

    increment() {
        const current = this.value || 0;
        if(current < this._max) {
            this.setDirty(true);
            this.value = current + this._scale;
            $trigger(this, 'value', this.value);
            $trigger(this, 'input');
        }
    }

    protected onText(text: string, notify: boolean) {
        this._value = text ? ((this._decimal || this._scale > 1) ? parseFloat(text) : parseInt(text)) : undefined;
        this.fixDesign();
        this.doCheck();
        $trigger(this, 'value', this._value == undefined ? undefined : (this._decimal ? this._value * this._scale : Math.round(this._value * this._scale)));
        if(notify)
            $trigger(this, 'input');
    }

    get value() {
        if(this._value == undefined)
            return undefined;

        return (this._decimal ? this._value * this._scale : Math.round(this._value * this._scale));
    }

    set value(v: number | undefined) {
        if(typeof v === 'string')
            v = v ? parseInt(v) : undefined;

        if(v == undefined) {
            this.setText('', false);
        } else {
            this._value = v / this._scale;
            this.setText('' + this._value, false);
        }
    }

    private calcOkay(): boolean {
        if(this._value == undefined)
            return !this.required;
        return this._value >= this._min && this._value <= this._max;
    }

    protected doCheck() {
        this.setOkay(this.calcOkay())
    }
}

interface UIFixedCurrencyInputOptions extends UITextBasedInputOptions<YPaymentAmount> {
    default_currency: YPaymentCurrency
}

export class UIFixedCurrencyInput extends UITextBasedInput<YPaymentAmount> {
    private _value: YPaymentAmount;
    private _default_currency: YPaymentCurrency;
    private _r_deco: HTMLElement;

    constructor(options: UIFixedCurrencyInputOptions) {
        const regexp = /[^-0-9.]/g;

        options.fixer = (text, pos) => {
            const fixed = text.replace(regexp, '');
            if(text != fixed)
                return [fixed, pos > 0 ? pos - 1 : pos];
            return;
        }

        options.l_deco = $el.I(options.default_currency);

        super(options);

        this._r_deco = options.l_deco;
        this._default_currency = options.default_currency;
        this._value = { value: 0, currency: options.default_currency }

        onKey(this, e => {
            if(e.key === UIKey.Down) {
                this.decrement();
                return true;
            } else if(e.key === UIKey.Up) {
                this.increment();
                return true;
            }
            return;
        });

        this.doCheck();
    }

    decrement() {
        this.setDirty(true);
        const v = this._value;
        this.value = { value: v.value - 100, currency: v.currency };
        $trigger(this, 'value', this.value);
        $trigger(this, 'input');
    }

    increment() {
        this.setDirty(true);
        const v = this._value;
        this.value = { value: v.value + 100, currency: v.currency };
        $trigger(this, 'value', this.value);
        $trigger(this, 'input');
    }

    protected onText(text: string, notify: boolean) {
        this._value.value = text ? Math.round(parseFloat(text) * 100) : 0;
        this.fixDesign();
        this.doCheck();
        $trigger(this, 'value', this._value);
        if(notify)
            $trigger(this, 'input');
    }

    get value(): YPaymentAmount {
        return this._value;
    }

    set value(v: YPaymentAmount | undefined) {
        v = v ? { value: v.value, currency: v.currency || this._default_currency } : { value: 0, currency: this._default_currency };
        if(v.currency != this._value.currency)
            this._r_deco.textContent = v.currency;
        this._value = v;
        this.setText('' + v.value / 100, false);
    }

    private calcOkay(): boolean {
        return true;
    }

    protected doCheck() {
        this.setOkay(this.calcOkay())
    }
}

export interface UISearchInputOptions extends UITextInputOptions {
}

export class UISearchInput extends UITextInput {
    constructor(options: UITextInputOptions = {}) {
        if(options.clear_button)
            options.l_deco = $el.ICON(UIIcon.MAGNIFYING_GLASS);
        else
            options.r_deco = $el.ICON(UIIcon.MAGNIFYING_GLASS);

        super(options);
    }

    get words() {
        return this.value ? this.value.split(' ').filter(w => w) : [];
    }

    filterArray<Model>(array: Model[], predicate: (m: Model, w: string) => boolean): Model[]
    filterArray<Model>(array: readonly Model[], predicate: (m: Model, w: string) => boolean): readonly Model[]
    filterArray<Model>(array: readonly Model[], predicate: (m: Model, w: string) => boolean) {
        const words = this.words.map(w => w.toLowerCase());
        if(!words.length) return array;

        return array.filter(m => {
            for(const w of words) {
                if(!predicate(m, w)) return false;
            }

            return true;
        });
    }

    getFilter<Model>(predicate: (m: Model, w: string) => boolean): ((value: Model) => boolean) | undefined {
        const words = this.words.map(w => w.toLowerCase());
        if(!words.length) return;

        return (m: Model) => {
            for(const w of words) {
                if(!predicate(m, w)) return false;
            }

            return true;
        };
    }
}

export class UIInputStatus extends UIView {
    private _status?: boolean;

    constructor(status?: boolean) {
        super('div', 'ui-input-status');

        this.ui_add([
            $el.ICON(UIIcon.CHECK, 'r-good'),
            $el.ICON(UIIcon.XMARK, 'r-bad')
        ]);
        this.status = status;
    }

    set status(s: boolean | undefined) {
        if(s === this._status) return;

        this._status = s;
        this.classList.toggle('s-good', s === true);
        this.classList.toggle('s-bad', s === false);
    }

    get status() {
        return this._status;
    }
}
